import { useEffect } from 'react';
import { create } from 'zustand';
import { endpoints } from '@/api';
import type { Me } from '@/api/types';
import { useAuth } from '@/stores/auth';
import { useRemaining } from '@/components/ui/Countdown';

/** A rewarded filter pass is running (SPEC_V5 §2). */
export function filterPassRunning(me: Me | null | undefined, now: number = Date.now()): boolean {
  const u = me?.filter_pass_until;
  if (!u) return false;
  const t = Date.parse(u);
  return !Number.isNaN(t) && t > now;
}

/** Filters may be edited and applied: Plus, or a running pass. */
export function canUseFilters(me: Me | null | undefined, now: number = Date.now()): boolean {
  return me?.plan === 'plus' || filterPassRunning(me, now);
}

interface LapseState {
  /** The pass that just ran out (for the quiet "filters paused" line). */
  lapsedUntil: string | null;
  setLapsed: (u: string | null) => void;
}
export const useFilterPassLapse = create<LapseState>((set) => ({ lapsedUntil: null, setLapsed: (lapsedUntil) => set({ lapsedUntil }) }));

let lapsing: string | null = null;

/**
 * Live view of the pass. When it runs out, filters quietly stop applying:
 * Me is updated locally (no toast) and refreshed from the server.
 */
export function useFilterPass(): { until: string | null; remainingMs: number; active: boolean } {
  const me = useAuth((s) => s.me);
  const until = me?.plan === 'plus' ? null : (me?.filter_pass_until ?? null);
  const remainingMs = useRemaining(until, 1000);
  const active = !!until && remainingMs > 0;

  useEffect(() => {
    if (!until || remainingMs > 0 || lapsing === until) return;
    lapsing = until;
    const cur = useAuth.getState().me;
    if (cur && cur.filter_pass_until === until) {
      useAuth.getState().setMe({ ...cur, filter_pass_until: null, filters_active: cur.plan === 'plus' && cur.filters_active });
      useFilterPassLapse.getState().setLapsed(until);
    }
    void endpoints
      .me()
      .then((m) => useAuth.getState().setMe(m))
      .catch(() => undefined);
  }, [until, remainingMs]);

  return { until, remainingMs, active };
}
